import React, { useCallback } from 'react';
import CatalogoCrudDialog from './CatalogoCrudDialog';
import vehiculoService, {
  CatalogoItemDto,
  MAX_UNIDAD_CODIGO,
  VehiculoReadDto,
} from '../../services/vehiculoService';

export interface UnidadCrudDialogProps {
  open: boolean;
  onClose: () => void;
  onSelect: (item: CatalogoItemDto) => void;
  /** Cliente al que pertenecen / se asignan las unidades. */
  nroCliente: number;
}

const normalizeUnidadCodigo = (value: string) => value.trim().slice(0, MAX_UNIDAD_CODIGO);

const toItem = (v: VehiculoReadDto): CatalogoItemDto => ({
  codigo: (v.patente || '').trim(),
  descripcion:
    [v.descripcion, v.descripcionMarca, v.descripcionModelo].filter(Boolean).join(' · ') ||
    (v.patente || '').trim(),
});

const UnidadCrudDialog: React.FC<UnidadCrudDialogProps> = ({
  open,
  onClose,
  onSelect,
  nroCliente,
}) => {
  const list = useCallback(
    async (term: string) => {
      if (!nroCliente || nroCliente <= 0) return [];
      const data = await vehiculoService.getVehiculos({
        searchTerm: term || undefined,
        nroCliente,
        pageNumber: 1,
        pageSize: 100,
      });
      return data.items
        .filter((v) => Number(v.nroCliente) === Number(nroCliente))
        .map(toItem);
    },
    [nroCliente],
  );

  const create = useCallback(
    async (codigo: string, descripcion: string) => {
      const patente = normalizeUnidadCodigo(codigo);
      if (!patente) {
        throw { response: { data: { message: 'La unidad es requerida.' } } };
      }
      if (!nroCliente || nroCliente <= 0) {
        throw { response: { data: { message: 'El cliente es requerido para dar de alta la unidad.' } } };
      }
      const created = await vehiculoService.createVehiculo({
        patente,
        nroCliente,
        descripcion: descripcion.trim(),
      });
      return toItem(created);
    },
    [nroCliente],
  );

  const update = useCallback(
    async (codigo: string, descripcion: string) => {
      const patente = normalizeUnidadCodigo(codigo);
      const existing = await vehiculoService.getVehiculo(patente);
      await vehiculoService.updateVehiculo(patente, {
        nroCliente: nroCliente > 0 ? nroCliente : existing.nroCliente,
        descripcion: descripcion.trim(),
        kilometrajeUltimoService: existing.kilometrajeUltimoService ?? null,
        fechaUltimoService: existing.fechaUltimoService ?? null,
        fechaProximoService: existing.fechaProximoService ?? null,
        anio: existing.anio ?? null,
        nroSerieMotor: existing.nroSerieMotor || '',
        nroSerieChasis: existing.nroSerieChasis || '',
        codigoTecnico: existing.codigoTecnico || '',
        codigoMarca: existing.codigoMarca || '',
        codigoLinea: existing.codigoLinea || '',
        codigoModelo: existing.codigoModelo || '',
        codigoCombustible: existing.codigoCombustible || '',
      });
    },
    [nroCliente],
  );

  const remove = useCallback(async (codigo: string) => {
    await vehiculoService.deleteVehiculo(normalizeUnidadCodigo(codigo));
  }, []);

  return (
    <CatalogoCrudDialog
      key={`unidades-cliente-${nroCliente}`}
      open={open}
      onClose={onClose}
      onSelect={onSelect}
      title="Unidades"
      codigoLabel="Unidad"
      nombreLabel="Descripción"
      codigoMaxLength={MAX_UNIDAD_CODIGO}
      list={list}
      create={create}
      update={update}
      remove={remove}
    />
  );
};

export default UnidadCrudDialog;
